import type { ProjectStatus } from "../redux/portfolio.types"

/**
 * The four counts §4's summary band leads with.
 *
 * Every one is derived from a metric the backend already resolves — nothing
 * here invents a figure. A count whose metric did not resolve comes back as
 * `null`, and the band omits that tile rather than showing a zero: "0 pending
 * approvals" and "approvals could not be read" are different statements, and
 * only one of them is true.
 */

/** A row of the `projects.upcoming_milestones` rows metric. */
export interface UpcomingMilestoneRow {
  project?: string | null
  phase?: string | null
  end_date?: string | null
  status?: string | null
  drill_to?: string | null
}

/**
 * A bare `YYYY-MM-DD` is a calendar day, not an instant. Parsing it with
 * `new Date()` reads it as UTC midnight, which lands on the previous day for
 * every account west of Greenwich — the repeated date-slip this codebase has
 * fixed several times. Split and compare the parts instead.
 */
const dayNumber = (iso: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  return (
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) /
    86_400_000
  )
}

/** `today` is the account's own calendar day, as `YYYY-MM-DD`. */
export const countMilestonesDueWithin = (
  rows: readonly UpcomingMilestoneRow[] | null | undefined,
  days: number,
  today: string
): number | null => {
  if (!rows) return null
  const start = dayNumber(today)
  if (start == null) return null
  // Inclusive of today and of the last day in the window: "this week" means
  // the next seven days a person would count on a calendar, today included.
  const end = start + days - 1
  return rows.filter((row) => {
    if (!row.end_date) return false
    const due = dayNumber(row.end_date)
    return due != null && due >= start && due <= end
  }).length
}

/**
 * Projects the portfolio flags as not on track.
 *
 * Deliberately the breakdown's own `at_risk + delayed` rather than a second
 * rule of this app's: the donut, this count and the project rows' health
 * badges must never disagree about how many projects are in trouble.
 */
export const needAttentionCount = (
  status: Partial<ProjectStatus> | null | undefined
): number | null => {
  if (!status) return null
  // Strict on purpose. Defaulting a missing field to 0 is what makes a wrong
  // payload shape render a confident "0 need attention" instead of omitting
  // the tile — the exact zero-vs-unknown confusion this module exists to
  // prevent, and it is invisible precisely because 0 looks like good news.
  const atRisk = status.at_risk
  const delayed = status.delayed
  if (typeof atRisk !== "number" || typeof delayed !== "number") return null
  if (!Number.isFinite(atRisk) || !Number.isFinite(delayed)) return null
  return atRisk + delayed
}

/**
 * What the Need attention tooltip has to say.
 *
 * §4 requires the count be explained, and the explanation has to name the
 * same scope the rest of the band uses, or the number reads as a different
 * population from Active projects beside it.
 */
export const NEED_ATTENTION_EXPLANATION =
  "Projects the portfolio flags as at risk or delayed, counted from the same projects, branch and filters as Active projects."
